import React, { useCallback, useMemo } from 'react';
import { MultiSelectFilter } from '@hud/web-components/Filters/Filters';
import useServicesAutocompleteQuery from '../../hooks/useServicesAutocompleteQuery';
import useEnvironment from '../../hooks/useEnvironment';
import { useDebouncedValue, useInputState } from '@mantine/hooks';
import { DEFAULT_DEBOUNCE_TIME } from '../../consts';
import { HudIcon } from '@hud/web-components/Icons/HudIcons';
import * as Styled from './ServicesFilter.styles';
import useAnalytics from '../../hooks/useAnalytics';
import { useGlobalQueryString } from '../../hooks/useGlobalFilter';
import HiddenServicesBadge from './HiddenServicesBadge';

export default function ServicesFilter({
  userPreferredServices,
  filteredServices,
  onFilteredServicesChange,
  handleFilterChange,
  multiSelectFilterElement,
}: {
  /**
   * A custom filter component to use instead of the default MultiSelectFilter
   * Must accept the same props as MultiSelectFilter
   */
  multiSelectFilterElement?: React.ComponentType<React.ComponentProps<typeof MultiSelectFilter>>;
  userPreferredServices: number[];
  filteredServices?: number[];

  onFilteredServicesChange?: (value: number[]) => void;
  handleFilterChange: (
    filterType: 'service',
    isActive: boolean,
    filterProps: object,
  ) => void;
}) {
  const analytics = useAnalytics();
  const { environment } = useEnvironment();
  const globalFilters = useGlobalQueryString();

  const [serviceSearchValue, setServiceSearchValue] = useInputState('');

  const [debouncedServiceSearchValue] = useDebouncedValue(
    serviceSearchValue,
    DEFAULT_DEBOUNCE_TIME,
  );

  const debouncedSearchTerms = useMemo(() => {
    return {
      searchTerms: debouncedServiceSearchValue.split(' ').filter(Boolean),
      environmentName: environment?.name,
      selectedIds: filteredServices,
    };
  }, [debouncedServiceSearchValue, environment?.name, filteredServices]);

  const serviceAutocompleteQuery =
    useServicesAutocompleteQuery(debouncedSearchTerms);

  const toggleServiceFilter = (isActive: boolean) => {
    handleFilterChange('service', isActive, { ids: filteredServices });
  };

  const getFilterNameById = useCallback((requestedId: number) => {
    return serviceAutocompleteQuery.data?.find(
      ({ id }) => id.toString() === requestedId.toString(),
    )?.name;
  }, [serviceAutocompleteQuery.data]);

  const filterLabel = useMemo(() => {
    if (
      userPreferredServices.length > 0 &&
      userPreferredServices.length === filteredServices?.length &&
      userPreferredServices.every((id) => filteredServices?.includes(id))
    ) {
      return 'My Services';
    }

    let label: string | React.ReactNode = 'All Services';

    if (filteredServices && filteredServices.length === 1) {
      const firstServiceName = getFilterNameById(filteredServices[0]);
      if (firstServiceName) {
        label = firstServiceName;
      }
    }

    if (filteredServices && filteredServices.length > 1) {
      const firstServiceName = getFilterNameById(filteredServices[0]);
      if (firstServiceName) {
        const hiddenServices = filteredServices.slice(1)
          .map(getFilterNameById)
          .filter((serviceName) => serviceName !== undefined);

        label = <>{firstServiceName}
          <HiddenServicesBadge
            numHiddenServices={filteredServices.length - 1}
            hiddenServices={hiddenServices}
          />
          </>;
      }

    };

    return label;
  }, [userPreferredServices, filteredServices, serviceAutocompleteQuery.data]);

  const FilterComponent = multiSelectFilterElement || MultiSelectFilter;

  return (
    <FilterComponent
      label="Services"
      key="service"
      mainGroupOption={{
        value: 'userServices',
        label: (
          <Styled.Container>
            <HudIcon icon="other.star" size="18px" />
            <span>My Services</span>
            <Styled.StyledLink
              to={`/explore/services?${globalFilters}`}
              onClick={(e) => {
                analytics.capture('my_services_filter_edit_clicked', {
                  services_count: userPreferredServices.length,
                });
                // prevent the modal from closing and running side effects,
                // we're only interested in navigating away
                e.stopPropagation();
              }}
            >
              Edit
            </Styled.StyledLink>
          </Styled.Container>
        ),
        relatedOptionsValues: userPreferredServices.map((id) => id.toString()),
        disabledTooltipDesc:
          'No services were selected in My Services. Choose the services relevant to you in the Services tab.',
      }}
      options={
        serviceAutocompleteQuery.data?.map(({ id, name }) => ({
          value: id.toString(),
          label: name,
        })) ?? []
      }
      onClear={() => {
        onFilteredServicesChange?.([]);
        toggleServiceFilter(false);
        analytics.capture('services_filter_removed');
      }}
      onChange={(value: string[]) => {
        const filteredValues = value.filter((x) => x !== 'userServices');

        let selected = value.includes('userServices')
          ? [...userPreferredServices]
          : [];

        selected = selected.concat(
          filteredValues.map((x) => Number.parseInt(x)),
        );

        // Call the onFilteredServicesChange function if provided
        onFilteredServicesChange?.(selected);
      }}
      onSearch={setServiceSearchValue}
      searchTerm={serviceSearchValue}
      onClose={() => setServiceSearchValue('')}
      multiSelectLabel={filterLabel}
      selectedOptions={filteredServices?.map((id) => id.toString()) ?? []}
    />
  );
}
